import { aspectValue, type Comic } from '@panels/shared';
import { useEffect, useMemo, useState } from 'react';
import { comicSchedule } from '../lib/replay.js';
import { PanelCanvas } from './PanelCanvas.js';

/**
 * A co-viewed replay (Round 3): every screen plays the comic off the same
 * instant. `startAt` is that instant on this device's performance.now()
 * timebase, already converted from the server clock; it may be in the past, in
 * which case the replay picks up where everyone else is.
 */
export interface StripSync {
  startAt: number;
  /** The driver skipped the replay for everyone. */
  skipped: boolean;
  /** The same instant on the server clock (epoch ms), for the start probe. */
  startAtServerMs?: number;
  /** Only the driver gets Skip replay in co-view. Omitted for everyone else. */
  onSkip?: () => void;
}

interface Props {
  comic: Comic;
  /** Replay-on-view: each panel draws itself, in order (§9). */
  replay?: boolean;
  /** Show author names under each panel. */
  attribution?: boolean;
  /** Tapping a panel (once the replay is done) opens it full size. */
  zoomable?: boolean;
  /** Play in step with everyone else, off a shared start time. */
  sync?: StripSync;
  /** The replay is over (or was skipped). */
  onFinished?: () => void;
}

/**
 * A finished comic, always laid out as a horizontal strip — that is what a comic
 * is. On a phone the panels are small; tapping one zooms it (when `zoomable`).
 * Panels replay one after another and a tap anywhere during the replay skips
 * straight to the finished strip. In co-view only the driver can skip, and it
 * skips for everyone.
 */
export function ComicStrip({ comic, replay = true, attribution = true, zoomable = false, sync, onFinished }: Props) {
  const panels = [...comic.panels].sort((a, b) => a.position - b.position);
  const [playing, setPlaying] = useState(replay ? 0 : panels.length);
  const [zoomed, setZoomed] = useState<number | null>(null);

  // Co-view: every panel's start comes from the one shared instant, never from
  // the panel before it finishing, so no drift accumulates along the strip.
  const schedule = useMemo(() => comicSchedule(panels.map((p) => p.ops)), [comic.sheetId]); // eslint-disable-line react-hooks/exhaustive-deps
  const syncEnd = sync ? sync.startAt + schedule.total : 0;
  const [syncDone, setSyncDone] = useState(() => !!sync && (sync.skipped || performance.now() >= syncEnd));

  useEffect(() => {
    setPlaying(replay ? 0 : panels.length);
    setZoomed(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [comic.sheetId, replay]);

  useEffect(() => {
    if (!sync) return;
    if (sync.skipped || performance.now() >= syncEnd) {
      setSyncDone(true);
      return;
    }
    setSyncDone(false);
    const timer = window.setTimeout(() => setSyncDone(true), syncEnd - performance.now());
    return () => clearTimeout(timer);
  }, [sync?.startAt, sync?.skipped, syncEnd]); // eslint-disable-line react-hooks/exhaustive-deps

  useStartProbe(sync && !sync.skipped ? sync.startAt : null, sync?.startAtServerMs ?? null, comic.sheetId);

  const finished = sync ? syncDone : playing >= panels.length;
  useEffect(() => {
    if (finished) onFinished?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finished]);

  const skip = sync ? sync.onSkip : () => setPlaying(panels.length);
  const zoomedPanel = zoomed !== null ? panels[zoomed] : null;

  return (
    <div className={`comic-strip panels-${panels.length} ${zoomable && finished ? 'zoomable' : ''}`}>
      <div className="comic-panels" onClick={finished || sync ? undefined : skip}>
        {!finished && skip ? (
          <button type="button" className="skip-replay" onClick={skip}>
            {sync ? 'Skip replay for everyone' : 'Skip replay'}
          </button>
        ) : null}

        {panels.map((panel, index) => {
          let canvas;
          if (sync) {
            canvas = (
              <PanelCanvas
                ops={panel.ops}
                aspectRatio={panel.aspectRatio}
                // Each panel stays blank until its moment on the shared clock.
                replay={!finished}
                replayStartAt={sync.startAt + schedule.starts[index]}
                placeholder={panel.status === 'submitted' ? undefined : 'unfinished'}
              />
            );
          } else {
            const revealed = index <= playing;
            const isPlaying = index === playing && !finished;
            canvas = (
              <PanelCanvas
                // Later panels stay blank until their turn, so the replay does not
                // spoil its own punchline.
                ops={revealed ? panel.ops : null}
                aspectRatio={panel.aspectRatio}
                replay={isPlaying}
                onReplayEnd={isPlaying ? () => setPlaying((n) => n + 1) : undefined}
                placeholder={panel.status === 'submitted' ? undefined : 'unfinished'}
              />
            );
          }
          return (
            <figure
              key={panel.panelId}
              className="comic-panel"
              onClick={finished && zoomable ? () => setZoomed(index) : undefined}
            >
              {canvas}
              {attribution ? (
                <figcaption>
                  <span className="panel-number">{panel.position}</span>
                  <span className="panel-author">{panel.author?.displayName ?? 'nobody'}</span>
                </figcaption>
              ) : null}
            </figure>
          );
        })}
      </div>

      {zoomedPanel ? (
        <div className="peek-overlay" onClick={() => setZoomed(null)}>
          <div
            className="peek-panel"
            style={{ width: `min(92vw, ${Math.round(80 * aspectValue(zoomedPanel.aspectRatio))}dvh)` }}
          >
            <PanelCanvas ops={zoomedPanel.ops} aspectRatio={zoomedPanel.aspectRatio} placeholder="unfinished" />
            <span className="draw-caption">
              Panel {zoomedPanel.position} — {zoomedPanel.author?.displayName ?? 'nobody'} · tap to close
            </span>
          </div>
        </div>
      ) : null}
    </div>
  );
}

/**
 * Measurement for co-viewed replays: the wall-clock time of the first animation
 * frame at or after the shared start, kept on `window.__panelsRevealStarts` (the
 * last 20). Comparing them across devices gives the real start spread. On the
 * machine that runs the API, firstFrameWallMs - startAtServerMs is exactly how
 * late this one started; lateMs is only the frame-timing part of that.
 */
function useStartProbe(startAt: number | null, startAtServerMs: number | null, sheetId: string) {
  useEffect(() => {
    if (startAt === null || performance.now() > startAt + 50) return;
    let frame = requestAnimationFrame(function tick(now) {
      if (now < startAt) {
        frame = requestAnimationFrame(tick);
        return;
      }
      const w = window as unknown as { __panelsRevealStarts?: unknown[] };
      const list = (w.__panelsRevealStarts ??= []);
      list.push({ sheetId, startAtServerMs, firstFrameWallMs: Date.now(), lateMs: Math.round(now - startAt) });
      if (list.length > 20) list.shift();
    });
    return () => cancelAnimationFrame(frame);
  }, [startAt, startAtServerMs, sheetId]);
}
